/**
 * Shared Salesforce attribute-path mapping (native / legacy-native / unified Apideck).
 * Client picker matching and server catalog / launch / hydrate resolve the same field
 * identity across dual-catalog paths through these helpers.
 */
import { GQL } from '@aline/graphql';

export type SalesforceFieldMapping = {
  fieldApiName: string;
  objectType: GQL.SalesforceFieldObjectType;
};

/**
 * A field on a customer-defined Salesforce object. `recordId` names one row of the object; null
 * for the field that blends every row the linked Opportunity relates to.
 */
export type SalesforceCustomObjectFieldMapping = {
  readonly fieldApiName: string;
  readonly objectApiName: string;
  readonly recordId: string | null;
};

export const NATIVE_ATTRIBUTE_PATH_PREFIX = 'native';
export const LEGACY_NATIVE_ATTRIBUTE_PATH_PREFIX = 'salesforce';

/**
 * Custom object fields live under a four-segment path (five with a row's record id) so they never
 * collide with the three-segment `native.<account|opportunity|user>.<Field>` paths of the standard
 * catalog.
 */
export const SALESFORCE_CUSTOM_OBJECT_PATH_PREFIX = `${NATIVE_ATTRIBUTE_PATH_PREFIX}.customObjects`;

const CUSTOM_OBJECT_PATH_SEGMENTS = 4;
const CUSTOM_OBJECT_ROW_PATH_SEGMENTS = 5;

const NATIVE_SEGMENT_TO_OBJECT_TYPE: Record<string, GQL.SalesforceFieldObjectType> = {
  account: GQL.SalesforceFieldObjectType.Account,
  opportunity: GQL.SalesforceFieldObjectType.Opportunity,
  opportunityLineItem: GQL.SalesforceFieldObjectType.OpportunityLineItem,
  product2: GQL.SalesforceFieldObjectType.Product2,
  user: GQL.SalesforceFieldObjectType.User,
};

const UNIFIED_PATH_TO_SALESFORCE_FIELD: Record<string, SalesforceFieldMapping> = {
  'opportunity.closeDate': {
    fieldApiName: 'CloseDate',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.company.addresses': {
    fieldApiName: 'BillingAddress',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.annualRevenue': {
    fieldApiName: 'AnnualRevenue',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.currency': {
    fieldApiName: 'CurrencyIsoCode',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.description': {
    fieldApiName: 'Description',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.emails': {
    fieldApiName: 'PersonEmail',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.firstName': {
    fieldApiName: 'FirstName',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.industry': {
    fieldApiName: 'Industry',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.lastName': {
    fieldApiName: 'LastName',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.name': {
    fieldApiName: 'Name',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.numberOfEmployees': {
    fieldApiName: 'NumberOfEmployees',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.ownership': {
    fieldApiName: 'Ownership',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.phoneNumbers': {
    fieldApiName: 'Phone',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.type': {
    fieldApiName: 'Type',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.company.websites': {
    fieldApiName: 'Website',
    objectType: GQL.SalesforceFieldObjectType.Account,
  },
  'opportunity.currency': {
    fieldApiName: 'CurrencyIsoCode',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.description': {
    fieldApiName: 'Description',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.expectedRevenue': {
    fieldApiName: 'ExpectedRevenue',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.leadSource': {
    fieldApiName: 'LeadSource',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.monetaryAmount': {
    fieldApiName: 'Amount',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.owner.addresses': {
    fieldApiName: 'Address',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.description': {
    fieldApiName: 'AboutMe',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.emails': {
    fieldApiName: 'Email',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.firstName': {
    fieldApiName: 'FirstName',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.language': {
    fieldApiName: 'LanguageLocaleKey',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.lastName': {
    fieldApiName: 'LastName',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.phoneNumbers': {
    fieldApiName: 'Phone',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.status': {
    fieldApiName: 'IsActive',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.owner.title': {
    fieldApiName: 'Title',
    objectType: GQL.SalesforceFieldObjectType.User,
  },
  'opportunity.priority': {
    fieldApiName: 'Priority__c',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.status': {
    fieldApiName: 'StageName',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.title': {
    fieldApiName: 'Name',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.type': {
    fieldApiName: 'Type',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
  'opportunity.winProbability': {
    fieldApiName: 'Probability',
    objectType: GQL.SalesforceFieldObjectType.Opportunity,
  },
};

const UNIFIED_FIELD_NAMES_BY_OBJECT_TYPE = Object.values(UNIFIED_PATH_TO_SALESFORCE_FIELD).reduce<
  Map<GQL.SalesforceFieldObjectType, Set<string>>
>((acc, mapping) => {
  const fieldApiNames = acc.get(mapping.objectType) ?? new Set<string>();
  fieldApiNames.add(mapping.fieldApiName);
  return acc.set(mapping.objectType, fieldApiNames);
}, new Map());

const NO_UNIFIED_FIELDS: ReadonlySet<string> = new Set();

const CUSTOM_FIELD_PATH_PATTERN =
  /^opportunity(?:\.(?<company>company))?\.customFields\.(?<fieldApiName>[^.]+)$/;

const CRM_IDENTIFIER_PATTERN = /^[A-Za-z0-9_]+$/;

/**
 * Resolves a document variable attributePath to a Salesforce field mapping.
 * Accepts native / legacy-native paths, Apideck unified standard paths, and
 * Apideck unified custom paths (`opportunity.customFields.X__c`).
 */
export function mapAttributePathToSalesforceField(
  attributePath: string
): SalesforceFieldMapping | null {
  const parts = attributePath.split('.');
  if (parts.length === 3) {
    const [prefix, segment, fieldApiName] = parts;
    if (
      (prefix === NATIVE_ATTRIBUTE_PATH_PREFIX || prefix === LEGACY_NATIVE_ATTRIBUTE_PATH_PREFIX) &&
      segment &&
      fieldApiName &&
      CRM_IDENTIFIER_PATTERN.test(fieldApiName)
    ) {
      const objectType = NATIVE_SEGMENT_TO_OBJECT_TYPE[segment];
      if (objectType) return { fieldApiName, objectType };
    }
  }

  const unified = UNIFIED_PATH_TO_SALESFORCE_FIELD[attributePath];
  if (unified) return unified;

  const customMatch = attributePath.match(CUSTOM_FIELD_PATH_PATTERN);
  if (!customMatch?.groups?.fieldApiName) return null;
  if (!CRM_IDENTIFIER_PATTERN.test(customMatch.groups.fieldApiName)) return null;

  return {
    fieldApiName: customMatch.groups.fieldApiName,
    objectType: customMatch.groups.company
      ? GQL.SalesforceFieldObjectType.Account
      : GQL.SalesforceFieldObjectType.Opportunity,
  };
}

/**
 * `native.customObjects.<ObjectApiName>.<FieldApiName>` for one custom object field, or
 * `native.customObjects.<ObjectApiName>.<RecordId>.<FieldApiName>` for that field on one row.
 */
export function buildSalesforceCustomObjectFieldPath(
  objectApiName: string,
  fieldApiName: string,
  recordId: string | null = null
): string {
  const row = recordId ? `.${recordId}` : '';
  return `${SALESFORCE_CUSTOM_OBJECT_PATH_PREFIX}.${objectApiName}${row}.${fieldApiName}`;
}

/**
 * Resolves a custom object attribute path to its object and field API names, and the row it
 * names if any, or null for every other path (standard native, legacy native, unified).
 */
export function mapAttributePathToSalesforceCustomObjectField(
  attributePath: string
): SalesforceCustomObjectFieldMapping | null {
  const parts = attributePath.split('.');
  const isRowPath = parts.length === CUSTOM_OBJECT_ROW_PATH_SEGMENTS;
  if (parts.length !== CUSTOM_OBJECT_PATH_SEGMENTS && !isRowPath) return null;
  const [prefix, segment, objectApiName, ...rest] = parts;
  if (`${prefix}.${segment}` !== SALESFORCE_CUSTOM_OBJECT_PATH_PREFIX) return null;
  const [recordId, fieldApiName] = isRowPath ? rest : [null, rest[0]];
  if (!objectApiName || !fieldApiName) return null;
  const identifiers = recordId
    ? [objectApiName, recordId, fieldApiName]
    : [objectApiName, fieldApiName];
  if (!identifiers.every(identifier => CRM_IDENTIFIER_PATTERN.test(identifier))) return null;
  return { fieldApiName, objectApiName, recordId: recordId ?? null };
}

/**
 * Salesforce fields backing unified Apideck paths. The native catalog keeps these even when a
 * page layout hides them — otherwise a unified field whose Apideck value is empty (e.g.
 * `opportunity.expectedRevenue`) has no SOQL value to fall back to and always renders blank.
 */
export function unifiedSalesforceFieldNames(
  objectType: GQL.SalesforceFieldObjectType
): ReadonlySet<string> {
  return UNIFIED_FIELD_NAMES_BY_OBJECT_TYPE.get(objectType) ?? NO_UNIFIED_FIELDS;
}

/**
 * Salesforce field identity key (`${objectType}.${fieldApiName}`), or the raw path
 * when the attribute path does not map to a Salesforce field.
 */
export function salesforceFieldIdentityKey(attributePath: string): string {
  const mapping = mapAttributePathToSalesforceField(attributePath);
  if (!mapping) return attributePath;
  return `${mapping.objectType}.${mapping.fieldApiName}`;
}

/**
 * True for native (`native.*`) or legacy-native (`salesforce.*`) 3-segment paths.
 * Unified Apideck paths return false so SOQL hydration does not treat them as native.
 */
export function isNativeSalesforceAttributePath(attributePath: string): boolean {
  const parts = attributePath.split('.');
  if (parts.length !== 3) return false;
  const [prefix, segment, fieldApiName] = parts;
  if (
    (prefix !== NATIVE_ATTRIBUTE_PATH_PREFIX && prefix !== LEGACY_NATIVE_ATTRIBUTE_PATH_PREFIX) ||
    !segment ||
    !fieldApiName
  ) {
    return false;
  }
  return Boolean(NATIVE_SEGMENT_TO_OBJECT_TYPE[segment]);
}

/**
 * True when two attribute paths refer to the same Salesforce field, including
 * legacy `salesforce.*` ↔ `native.*` and unified `opportunity.type` ↔ `native.opportunity.Type`.
 */
export function salesforceAttributePathsAreEquivalent(a: string, b: string): boolean {
  if (a === b) return true;
  const mappingA = mapAttributePathToSalesforceField(a);
  const mappingB = mapAttributePathToSalesforceField(b);
  return Boolean(
    mappingA &&
    mappingB &&
    mappingA.objectType === mappingB.objectType &&
    mappingA.fieldApiName === mappingB.fieldApiName
  );
}
